<script setup lang="ts">
/** The landing: a neighbour page in development, its own address once deployed. */
const landingUrl = typeof __VP_LANDING_URL__ === 'string' ? __VP_LANDING_URL__ : 'lending/';
import { ask, tell } from '../dialogs';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useApp } from '../stores/app';
import { legalState, loadLegal } from '../legal';
import ModulesPanel from '../components/ModulesPanel.vue';
import LogsView from './LogsView.vue';
import { LOCALES, locale, setLocale, t } from '../i18n';
import { browserStorage, CANVAS_BACKGROUNDS, logger, saveSettingsNow, settings, storage } from '../platform';
import { storagePlace } from '../storage-location';
import { pwa } from '../pwa';
import { formatBytes, tn } from '../i18n';
import { ACCENTS, accentColors, contrast, isDark } from '../theme';
import { exportAllCanvases, importArchiveFile } from '../archive';
import { API_VERSION } from '@vp/module-runtime';
import { DATA_FORMAT_VERSION, type StorageUsage } from '@vp/core';
import { LANG_VERSION } from '@vp/dskhema';
import { ui } from '../editor/bridge';
import { pinSet, profileCard, saveProfileCard } from '../profile-card';
import ProfileAvatar from '../components/ProfileAvatar.vue';
import ProfileSecurity from '../components/ProfileSecurity.vue';
import ProfileDeleteForm from '../components/ProfileDeleteForm.vue';
import { pickPicture, pictureFromFile, PictureError } from '../profiles/picture';
import { exportProfile, importProfileFile } from '../profiles/transfer';
import { switchProfile } from '../profiles/session';
import { profileState } from '../profiles/state';
import { ProfileLimitError, MAX_PROFILES } from '../profiles/registry';
import { chatStorageSummary, clearAllChats, clearCanvasChat, clearInboxChat, type ChatStorageEntry } from '../chat';
import AppNav from '../components/AppNav.vue';
import { readHomeLayout, writeHomeLayout, type HomeLayout } from '../home-layout';
import Icon from '../components/Icon.vue';
import Toggle from '../components/Toggle.vue';

/**
 * App settings (§6.8): a side list of sections and the selected section's options;
 * each option has a short explanation on the left and its control on the right.
 */
type SectionId = 'profile' | 'general' | 'appearance' | 'canvas' | 'control' | 'modules' | 'storage' | 'perf' | 'dev' | 'about';
const SECTIONS: { id: SectionId; icon: string; label: string }[] = [
  { id: 'profile', icon: 'user', label: 'app.profile' },
  { id: 'general', icon: 'language', label: 'settings.secGeneral' },
  { id: 'appearance', icon: 'palette', label: 'settings.secAppearance' },
  { id: 'canvas', icon: 'grid', label: 'settings.secCanvas' },
  { id: 'control', icon: 'select', label: 'settings.secControl' },
  { id: 'modules', icon: 'module', label: 'modules.title' },
  { id: 'storage', icon: 'storage', label: 'settings.secStorage' },
  { id: 'perf', icon: 'gauge', label: 'settings.secPerformance' },
  { id: 'dev', icon: 'code', label: 'settings.secDeveloper' },
  { id: 'about', icon: 'info', label: 'settings.secAbout' },
];
/** `#/logs` and a remembered "logs" section open For developers, where the journal lives. */
function asSection(v: string | null): SectionId | null {
  if (v === 'logs') return 'dev';
  return v && SECTIONS.some((x) => x.id === v) ? (v as SectionId) : null;
}
const app = useApp();
/** Profile → Security is layout only: the window opens, its buttons do not act yet. */
/** A picture for the profile avatar, cut square and made small. */
async function chooseAvatar(): Promise<void> {
  const file = await pickPicture();
  if (!file) return;
  try {
    profileCard.avatar = await pictureFromFile(file);
  } catch (e) {
    app.notify('error', e instanceof PictureError && e.message === 'size' ? t('profiles.pictureTooBig') : t('profiles.pictureBad'));
  }
}
const profileBusy = ref(false);
const savingSettings = ref(false);
async function saveAllSettings(): Promise<void> {
  if (savingSettings.value) return;
  savingSettings.value = true;
  try {
    await Promise.all([saveProfileCard(), saveSettingsNow()]);
    app.notify('info', t('canvas.saved'));
  } catch {
    app.notify('error', t('canvas.saveError'));
  } finally {
    savingSettings.value = false;
  }
}
async function doExportProfile(): Promise<void> {
  profileBusy.value = true;
  try {
    await exportProfile();
  } catch (e) {
    app.notify('error', t('profiles.exportFailed', { msg: e instanceof Error ? e.message : String(e) }));
  } finally {
    profileBusy.value = false;
  }
}
async function doImportProfile(): Promise<void> {
  try {
    const p = await importProfileFile();
    if (!p) return;
    app.notify('info', t('profiles.imported', { name: p.firstName }), 10000, [{ label: t('profiles.switch'), run: switchProfile }]);
  } catch (e) {
    app.notify('error', e instanceof ProfileLimitError ? t('profiles.limit', { n: MAX_PROFILES }) : t('profiles.importFailed', { msg: e instanceof Error ? e.message : String(e) }));
  }
}
const deleteOpen = ref(false);
/** The profile is gone: every tab goes to the profile list (or the creation screen). */
async function profileDeleted(folderOk: boolean): Promise<void> {
  deleteOpen.value = false;
  if (!folderOk) await tell(t('profiles.deletedFolderLeft'));
  window.location.reload();
}
const securityOpen = ref(false);
const securityEl = ref<HTMLElement | null>(null);
watch(securityOpen, async (open) => {
  if (!open) return;
  await nextTick();
  securityEl.value?.focus();
});
/** Profile → Chat settings: saved chat lines per canvas, removable one canvas at a time or all at once. */
const chatsOpen = ref(false);
const chatsEl = ref<HTMLElement | null>(null);
const chatSummary = ref<ChatStorageEntry[]>([]);
const chatRows = computed(() =>
  chatSummary.value
    .map((entry) => ({
      ...entry,
      name: entry.canvasId === null ? t('chatSettings.feed') : (app.canvases.find((c) => c.id === entry.canvasId)?.name ?? t('feed.canvasGone')),
    }))
    .sort((a, b) => (a.canvasId === null ? 1 : b.canvasId === null ? -1 : b.count - a.count)),
);
watch(chatsOpen, async (open) => {
  if (!open) return;
  chatSummary.value = chatStorageSummary();
  await nextTick();
  chatsEl.value?.focus();
});
async function clearRow(row: ChatStorageEntry & { name: string }): Promise<void> {
  if (!(await ask(t('chatSettings.confirmFor', { name: row.name }), { title: t('chatSettings.title'), okLabel: t('home.delete'), danger: true }))) return;
  if (row.canvasId === null) clearInboxChat();
  else clearCanvasChat(row.canvasId);
  chatSummary.value = chatStorageSummary();
}
async function clearChats(): Promise<void> {
  if (!(await ask(t('chatSettings.confirmAll'), { title: t('chatSettings.title'), okLabel: t('home.delete'), danger: true }))) return;
  clearAllChats();
  chatSummary.value = chatStorageSummary();
}
const active = ref<SectionId>(asSection(app.settingsSection) ?? 'profile');
// #/modules and #/logs (and the palette) open this section
watch(
  () => app.settingsSection,
  (s) => {
    const id = asSection(s);
    if (id) pick(id);
    app.settingsSection = null;
  },
  { immediate: true },
);
function pick(id: SectionId): void {
  active.value = id;
}
function onNavKey(e: KeyboardEvent): void {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const i = SECTIONS.findIndex((x) => x.id === active.value);
  const next = SECTIONS[(i + (e.key === 'ArrowDown' ? 1 : -1) + SECTIONS.length) % SECTIONS.length];
  pick(next.id);
  void nextTick(() => (document.getElementById('st-' + next.id) as HTMLElement | null)?.focus());
  e.preventDefault();
}
const version = __APP_VERSION__;
const verbose = ref(logger.verbose);
const remaining = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;

function tick(): void {
  verbose.value = logger.verbose;
  remaining.value = Math.ceil(logger.verboseRemainingMs / 60000);
}
onMounted(() => {
  if (!legalState.docs.length) void loadLegal();
  tick();
  timer = setInterval(tick, 1000);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

function toggleVerbose(on: boolean): void {
  logger.setVerbose(on);
  tick();
}

async function toggleDev(on: boolean): Promise<void> {
  if (on && !(await ask(t('settings.devModeWarn'), { title: t('settings.devMode') }))) return;
  settings.devMode = on;
  logger.info('settings.devMode', { on });
}

function setGrid(kind: 'none' | 'dots' | 'lines' | 'cells'): void {
  settings.editor.grid.enabled = kind !== 'none';
  if (kind !== 'none') settings.editor.grid.kind = kind;
}

const themes = ['system', 'light', 'dark'] as const;
const homeLayout = ref<HomeLayout>(readHomeLayout());
function chooseHomeLayout(next: HomeLayout): void {
  homeLayout.value = next;
  writeHomeLayout(next);
}

// ---------- accent colors ----------
const inkRows = [
  { key: 'onAccent' as const, label: 'settings.onAccent', hint: 'settings.onAccentHint', presets: ['#ffffff', '#0f172a'] },
  { key: 'accentInk' as const, label: 'settings.accentInk', hint: 'settings.accentInkHint', presets: ['#0f172a', '#1e3a8a', '#ffffff'] },
];
/** A hand-picked color that is hard to read (WCAG < 4.5:1): say so, with the ratio. */
const lowContrast = computed(() => {
  const c = accentColors.value;
  const panel = isDark.value ? '#172033' : '#ffffff';
  const ratios = [settings.onAccent ? contrast(c.onAccent, settings.accent) : 99, settings.accentInk ? contrast(c.ink, panel) : 99];
  const worst = Math.min(...ratios);
  return worst < 4.5 ? worst.toFixed(1) : '';
});

// ---------- storage ----------
const usage = ref<StorageUsage | null>(null);
const quota = ref<{ usage?: number; quota?: number }>({});
const lastExport = ref<number | null>(null);
const busy = ref(false);
const importInput = ref<HTMLInputElement | null>(null);
async function loadUsage(): Promise<void> {
  await app.refreshCanvases();
  usage.value = await storage.usage();
  // the whole browser (every profile), also when this profile keeps its canvases in a folder
  quota.value = await browserStorage.estimate();
  lastExport.value = (await storage.getSetting<number>('lastExportAt')) ?? null;
}
watch(active, (a) => a === 'storage' && void loadUsage(), { immediate: true });
const otherBytes = computed(() => (usage.value ? usage.value.previewBytes + usage.value.logBytes + usage.value.moduleBytes : 0));
const parts = computed(() => {
  const u = usage.value;
  if (!u) return [];
  const all = [
    { key: 'canvases', label: t('storage.canvases'), bytes: u.canvasBytes, color: 'var(--accent)' },
    { key: 'attachments', label: t('storage.attachments'), bytes: u.attachments.bytes, color: '#f59e0b' },
    { key: 'other', label: t('storage.other'), bytes: otherBytes.value, color: 'var(--text-3)' },
  ];
  const total = all.reduce((x, p) => x + p.bytes, 0) || 1;
  return all.map((p) => ({ ...p, pct: (p.bytes / total) * 100 }));
});
const ownTotal = computed(() => parts.value.reduce((x, p) => x + p.bytes, 0));
/** Biggest canvases first: content and pictures. */
const bigCanvases = computed(() => {
  const u = usage.value;
  if (!u) return [];
  const byId = new Map(u.canvases.map((c) => [c.id, c]));
  return app.canvases
    .map((c) => ({ id: c.id, name: c.name, objects: c.objectCount, bytes: byId.get(c.id)?.bytes ?? 0, attachments: byId.get(c.id)?.attachments ?? 0 }))
    .sort((a, b) => b.bytes + b.attachments - (a.bytes + a.attachments))
    .slice(0, 8);
});
const exportDays = computed(() => (lastExport.value ? Math.floor((Date.now() - lastExport.value) / 86400000) : null));
async function run(fn: () => Promise<unknown>): Promise<void> {
  busy.value = true;
  try {
    await fn();
  } finally {
    busy.value = false;
    await loadUsage();
  }
}
const exportAll = () => run(exportAllCanvases);
async function onImport(e: Event): Promise<void> {
  const f = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = '';
  if (f) await run(() => importArchiveFile(f, app));
}
const removeUnused = () => run(async () => app.notify('info', t('storage.unusedRemoved', { n: await storage.gcAttachments(0) })));
const clearLogs = () => run(() => storage.clearLogs());
const clearPreviews = () => run(() => storage.clearPreviews());
async function clearAll(): Promise<void> {
  const ok = await ask(t('storage.clearAllConfirm', { n: app.canvases.length }), { title: t('storage.clearAll'), okLabel: t('storage.clearAllOk'), danger: true });
  if (!ok) return;
  await run(async () => {
    for (const id of [...app.tabs]) await app.closeTab(id);
    await storage.clearCanvases();
    logger.info('storage.cleared', {});
    app.notify('info', t('storage.cleared'));
  });
}

// ---------- about ----------
const build = {
  version: __APP_VERSION__,
  number: __BUILD_NUMBER__,
  date: __BUILD_DATE__,
  type: __BUILD_TYPE__,
  id: __BUILD_ID__,
};
const buildDate = computed(() => new Date(build.date).toLocaleString(locale.value === 'ru' ? 'ru-RU' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
const KNOWN_TYPES = ['development', 'local', 'preview', 'beta', 'release'];
const aboutRows = computed(() => [
  [t('about.version'), build.version],
  [t('about.build'), build.number ? '#' + build.number : t('about.buildLocal')],
  [t('about.date'), buildDate.value],
  [t('about.type'), KNOWN_TYPES.includes(build.type) ? t('about.type_' + build.type) : build.type],
  [t('about.commit'), build.id === 'dev' ? '—' : build.id.slice(0, 10)],
  [t('about.renderer'), ui.backend === 'canvas2d' ? 'Canvas 2D' : 'WebGL 2'],
  [t('about.offline'), pwa.offlineReady ? (pwa.standalone ? t('about.offlineInstalled') : t('about.offlineYes')) : t('about.offlineNo')],
  [t('about.storagePlace'), storagePlace.kind === 'folder' ? t('storage.place.folder') + ': ' + storagePlace.name : t('storage.place.browser')],
  [t('about.api'), API_VERSION],
  [t('about.format'), DATA_FORMAT_VERSION],
  [t('about.dskhema'), String(LANG_VERSION)],
]);
async function copyAbout(): Promise<void> {
  const text = [t('app.title'), ...aboutRows.value.map(([k, v]) => k + ': ' + v), 'UA: ' + navigator.userAgent].join('\n');
  await navigator.clipboard.writeText(text).catch(() => undefined);
  app.notify('info', t('about.copied'));
}

function setStep(v: number): void {
  if (Number.isFinite(v)) settings.editor.grid.step = Math.min(1000, Math.max(2, Math.round(v)));
}
</script>

<template>
  <div class="wrap">
    <AppNav />
    <main class="page">
      <div class="page-inner layout">
        <nav class="side" :aria-label="t('settings.title')">
          <h1 class="title">{{ t('settings.title') }}</h1>
          <div role="tablist" aria-orientation="vertical" @keydown="onNavKey">
            <button
              v-for="sec in SECTIONS"
              :id="'st-' + sec.id"
              :key="sec.id"
              role="tab"
              class="nav-item"
              :class="{ active: active === sec.id }"
              :aria-selected="active === sec.id"
              :aria-controls="'sp-' + sec.id"
              :tabindex="active === sec.id ? 0 : -1"
              @click="pick(sec.id)"
            >
              <span class="nav-ico"><Icon :name="sec.icon" :size="16" /></span>
              <span class="nav-label">{{ t(sec.label) }}<span v-if="sec.id === 'profile'" class="beta">{{ t('app.beta') }}</span></span>
            </button>
          </div>
          <p class="version">v{{ version }}</p>
        </nav>
        <div class="main">
          <section v-if="active === 'profile'" id="sp-profile" class="group" role="tabpanel" aria-labelledby="st-profile">
            <div class="item">
              <div class="text">
                <span class="label">{{ t('profile.avatar') }}</span>
              </div>
              <div class="avatar-row">
                <ProfileAvatar :id="profileCard.id" :first-name="profileCard.firstName || '?'" :last-name="profileCard.lastName" :avatar="profileCard.avatar" :size="44" />
                <button type="button" class="pill" data-act="avatar-choose" @click="chooseAvatar">{{ t('profile.avatarChoose') }}</button>
                <button v-if="profileCard.avatar" type="button" class="pill" @click="profileCard.avatar = ''">{{ t('profiles.pictureRemove') }}</button>
              </div>
            </div>
            <div class="item">
              <div class="text">
                <span class="label">{{ t('profile.firstName') }}</span>
              </div>
              <input v-model="profileCard.firstName" class="input name" type="text" maxlength="80" autocomplete="given-name" :aria-label="t('profile.firstName')" />
            </div>
            <div class="item">
              <div class="text">
                <span class="label">{{ t('profile.lastName') }}</span>
              </div>
              <input v-model="profileCard.lastName" class="input name" type="text" maxlength="80" autocomplete="family-name" :aria-label="t('profile.lastName')" />
            </div>
            <div class="item">
              <div class="text">
                <span class="label">{{ t('chatSettings.title') }}</span>
                <span class="hint">{{ t('chatSettings.hint') }}</span>
              </div>
              <button type="button" class="pill" data-act="chat-settings" @click="chatsOpen = true">{{ t('chatSettings.open') }}</button>
            </div>
            <div class="item">
              <div class="text">
                <span class="label">{{ t('profile.security') }}</span>
                <span class="hint">{{ pinSet ? t('profiles.pinOn') : t('profiles.pinOff') }}</span>
              </div>
              <button type="button" class="pill" data-act="profile-security" :disabled="profileState.guest" @click="securityOpen = true">{{ t('profile.security') }}</button>
            </div>
            <div class="item">
              <div class="text">
                <span class="label">{{ t('profiles.onDevice') }}</span>
                <span class="hint">{{ t('profiles.onDeviceHint') }}</span>
              </div>
              <div class="inline">
                <button type="button" class="pill" data-act="switch-profile" :disabled="profileState.guest" @click="switchProfile">{{ t('profiles.switch') }}</button>
                <button type="button" class="pill" data-act="import-profile" :disabled="profileState.guest" @click="doImportProfile">{{ t('profiles.import') }}</button>
              </div>
            </div>
            <div class="item actions">
              <button type="button" class="pill danger" data-act="delete-profile-open" :disabled="profileState.guest" @click="deleteOpen = true">{{ t('profiles.delete') }}</button>
              <button type="button" class="pill" data-act="export-profile" :disabled="profileBusy || profileState.guest" @click="doExportProfile">{{ t('profiles.export') }}</button>
              <button type="button" class="pill primary" data-act="save-settings" :disabled="savingSettings" @click="saveAllSettings">{{ t('common.save') }}</button>
            </div>
          </section>
          <ModulesPanel v-if="active === 'modules'" />
        <section v-if="active === 'general'" :id="'sp-general'" class="group" role="tabpanel" :aria-labelledby="'st-general'">
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.language') }}</span>
              <span class="hint">{{ t('settings.languageHint') }}</span>
            </div>
            <div class="seg-pills" role="radiogroup" :aria-label="t('settings.language')">
              <button v-for="l in LOCALES" :key="l.id" role="radio" :aria-checked="locale === l.id" :class="{ active: locale === l.id }" @click="setLocale(l.id)">{{ l.name }}</button>
            </div>
          </div>
        </section>

        <section v-if="active === 'appearance'" :id="'sp-appearance'" class="group" role="tabpanel" :aria-labelledby="'st-appearance'">
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.theme') }}</span>
              <span class="hint">{{ t('settings.themeHint') }}</span>
            </div>
            <div class="seg-pills" role="radiogroup" :aria-label="t('settings.theme')">
              <button v-for="m in themes" :key="m" role="radio" :aria-checked="settings.theme === m" :class="{ active: settings.theme === m }" @click="settings.theme = m">
                <Icon :name="m === 'system' ? 'sunMoon' : m === 'light' ? 'sun' : 'moon'" :size="14" /><span class="seg-text">{{ t('settings.theme_' + m) }}</span>
              </button>
            </div>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.homeLayout') }}</span>
              <span class="hint">{{ t('settings.homeLayoutHint') }}</span>
            </div>
            <div class="seg-pills" role="radiogroup" :aria-label="t('settings.homeLayout')">
              <button type="button" role="radio" :aria-checked="homeLayout === 'grid'" :class="{ active: homeLayout === 'grid' }" @click="chooseHomeLayout('grid')">
                <Icon name="tiles" :size="14" /><span class="seg-text">{{ t('home.layoutGrid') }}</span>
              </button>
              <button type="button" role="radio" :aria-checked="homeLayout === 'list'" :class="{ active: homeLayout === 'list' }" @click="chooseHomeLayout('list')">
                <Icon name="list" :size="14" /><span class="seg-text">{{ t('home.layoutList') }}</span>
              </button>
            </div>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.glass') }}</span>
              <span class="hint">{{ t('settings.glassHint') }}</span>
            </div>
            <input v-model.number="settings.glass" type="range" min="0" max="100" step="1" :disabled="settings.noMotion" :aria-label="t('settings.glass')" />
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.noMotion') }}</span>
              <span class="hint">{{ t('settings.noMotionHint') }}</span>
            </div>
            <Toggle v-model="settings.noMotion" :label="t('settings.noMotion')" />
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.accent') }}</span>
              <span class="hint">{{ t('settings.accentHint') }}</span>
            </div>
            <div class="swatches">
              <button
                v-for="c in ACCENTS"
                :key="c"
                class="swatch-btn"
                :class="{ active: settings.accent === c }"
                :style="{ background: c }"
                :aria-label="c"
                :aria-pressed="settings.accent === c"
                @click="settings.accent = c"
              ></button>
              <label class="swatch-btn custom" :class="{ active: !ACCENTS.includes(settings.accent) }" :title="t('export.color')">
                <input v-model="settings.accent" type="color" :aria-label="t('settings.accent')" />
              </label>
            </div>
          </div>
          <div class="item sub">
            <div class="text">
              <span class="hint">{{ t('settings.accentPreview') }}</span>
            </div>
            <div class="inline" aria-hidden="true">
              <span class="pill active"><Icon name="check" :size="14" />{{ t('settings.accentSample') }}</span>
              <span class="pill tall accent-btn"><Icon name="plus" :size="14" />{{ t('home.newCanvas') }}</span>
            </div>
          </div>
          <div v-for="row in inkRows" :key="row.key" class="item sub">
            <div class="text">
              <span class="label">{{ t(row.label) }}</span>
              <span class="hint">{{ t(row.hint) }}<template v-if="!settings[row.key]"> — {{ t('settings.inkAutoNow') }}</template></span>
            </div>
            <div class="swatches">
              <button class="pill" :class="{ active: !settings[row.key] }" :aria-pressed="!settings[row.key]" @click="settings[row.key] = ''">{{ t('settings.inkAuto') }}</button>
              <button
                v-for="c in row.presets"
                :key="c"
                class="swatch-btn"
                :class="{ active: settings[row.key] === c }"
                :style="{ background: c }"
                :aria-label="c"
                :aria-pressed="settings[row.key] === c"
                @click="settings[row.key] = c"
              ></button>
              <label class="swatch-btn custom" :class="{ active: !!settings[row.key] && !row.presets.includes(settings[row.key]) }" :title="t('export.color')">
                <input :value="settings[row.key] || accentColors[row.key === 'onAccent' ? 'onAccent' : 'ink']" type="color" :aria-label="t(row.label)" @input="settings[row.key] = ($event.target as HTMLInputElement).value" />
              </label>
            </div>
          </div>
          <p v-if="lowContrast" class="note warn"><Icon name="warning" :size="14" />{{ t('settings.inkLowContrast', { ratio: lowContrast }) }}</p>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.darkCanvas') }}</span>
              <span class="hint">{{ t('settings.darkCanvasHint') }}</span>
            </div>
            <Toggle v-model="settings.darkCanvas" :label="t('settings.darkCanvas')" />
          </div>
        </section>

        <section v-if="active === 'storage'" :id="'sp-storage'" class="group" role="tabpanel" :aria-labelledby="'st-storage'">
          <div class="item place">
            <div class="text">
              <span class="label">{{ t('storage.place.title') }}</span>
              <span class="hint">{{ storagePlace.kind === 'folder' ? t('storage.place.folderHint') : t('storage.place.browserHint') }}</span>
            </div>
            <span class="place-value" data-place="current">
              <Icon :name="storagePlace.kind === 'folder' ? 'folder' : 'storage'" :size="14" />
              <span v-if="storagePlace.kind === 'folder'" class="folder-name" data-place="folder">{{ storagePlace.name }}</span>
              <span v-else>{{ t('storage.place.browser') }}</span>
            </span>
          </div>
          <div class="usage">
            <div class="usage-head">
              <span class="usage-who">{{ t('storage.profileUsage', { name: profileCard.firstName || '…' }) }}</span>
              <strong data-field="profile-usage">{{ usage ? formatBytes(ownTotal) : '…' }}</strong>
              <span v-if="quota.quota" class="hint" data-field="browser-usage">{{ t('storage.browser', { used: formatBytes(quota.usage), quota: formatBytes(quota.quota) }) }}</span>
            </div>
            <div class="usage-bar" role="img" :aria-label="parts.map((p) => p.label + ' ' + formatBytes(p.bytes)).join(', ')">
              <span v-for="p in parts" :key="p.key" :style="{ width: p.pct + '%', background: p.color }"></span>
            </div>
            <ul class="legend">
              <li v-for="p in parts" :key="p.key">
                <i :style="{ background: p.color }"></i>
                <span class="lg-label">{{ p.label }}</span>
                <b>{{ formatBytes(p.bytes) }}</b>
                <small v-if="p.key === 'canvases'">{{ tn('storage.canvasCount', app.canvases.length) }}</small>
                <small v-else-if="p.key === 'attachments' && usage">{{ tn('storage.fileCount', usage.attachments.count) }}</small>
                <small v-else>{{ t('storage.otherHint') }}</small>
              </li>
            </ul>
          </div>
          <div v-if="bigCanvases.length" class="item column">
            <span class="label">{{ t('storage.largest') }}</span>
            <table class="sizes">
              <tbody>
                <tr v-for="c in bigCanvases" :key="c.id">
                  <td class="name">{{ c.name }}</td>
                  <td class="muted">{{ tn('home.objects', c.objects) }}</td>
                  <td class="num">{{ formatBytes(c.bytes) }}</td>
                  <td class="muted num">{{ c.attachments ? '+ ' + formatBytes(c.attachments) + ' ' + t('storage.ofFiles') : '' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('storage.backup') }}</span>
              <span class="hint">{{ exportDays === null ? t('home.lastExportNever') : t('home.lastExport', { days: exportDays }) }}. {{ storagePlace.kind === 'folder' ? t('storage.backupHintFolder') : t('storage.backupHint') }}</span>
            </div>
            <div class="inline">
              <button class="pill tall" :disabled="busy" @click="importInput?.click()"><Icon name="upload" :size="14" />{{ t('home.import') }}</button>
              <button class="pill tall accent-btn" :disabled="busy || !app.canvases.length" @click="exportAll"><Icon name="download" :size="14" />{{ t('home.exportAll') }}</button>
              <input ref="importInput" type="file" accept=".vpz,.zip,application/zip" hidden @change="onImport" />
            </div>
          </div>
          <div v-if="usage && usage.attachments.unused" class="item">
            <div class="text">
              <span class="label">{{ t('storage.unused') }}</span>
              <span class="hint">{{ t('storage.unusedHint', { n: usage.attachments.unused, size: formatBytes(usage.attachments.unusedBytes) }) }}</span>
            </div>
            <button class="pill tall" :disabled="busy" @click="removeUnused">{{ t('storage.remove') }}</button>
          </div>
          <div v-if="usage && usage.logBytes" class="item">
            <div class="text">
              <span class="label">{{ t('storage.logs') }}</span>
              <span class="hint">{{ t('storage.logsHint', { size: formatBytes(usage.logBytes) }) }}</span>
            </div>
            <button class="pill tall" :disabled="busy" @click="clearLogs">{{ t('storage.clear') }}</button>
          </div>
          <div v-if="usage && usage.previewBytes" class="item">
            <div class="text">
              <span class="label">{{ t('storage.previews') }}</span>
              <span class="hint">{{ t('storage.previewsHint', { size: formatBytes(usage.previewBytes) }) }}</span>
            </div>
            <button class="pill tall" :disabled="busy" @click="clearPreviews">{{ t('storage.clear') }}</button>
          </div>
          <div class="item danger-zone">
            <div class="text">
              <span class="label">{{ t('storage.clearAll') }}</span>
              <span class="hint">{{ t('storage.clearAllHint') }}</span>
            </div>
            <button class="pill tall danger-btn" :disabled="busy || !app.canvases.length" @click="clearAll"><Icon name="trash" :size="14" />{{ t('storage.clearAllOk') }}</button>
          </div>
          <div class="item actions">
            <button type="button" class="pill danger" data-act="delete-profile-open" :disabled="profileState.guest" @click="deleteOpen = true">{{ t('profiles.delete') }}</button>
            <button type="button" class="pill" data-act="export-profile" :disabled="profileBusy || profileState.guest" @click="doExportProfile">{{ t('profiles.export') }}</button>
          </div>
        </section>

        <section v-if="active === 'about'" :id="'sp-about'" class="group" role="tabpanel" :aria-labelledby="'st-about'">
          <div class="about-head">
            <svg viewBox="0 0 32 32" width="44" height="44" aria-hidden="true">
              <rect x="3" y="3" width="26" height="26" rx="7" fill="var(--accent)" />
              <rect x="9" y="9" width="8" height="8" rx="1.5" fill="var(--on-accent)" />
              <circle cx="21" cy="21" r="4" fill="var(--on-accent)" />
            </svg>
            <div class="text">
              <span class="label">{{ t('app.title') }}</span>
              <span class="hint">{{ t('about.tagline') }}</span>
            </div>
          </div>
          <dl class="about">
            <template v-for="[k, v] in aboutRows" :key="k">
              <dt>{{ k }}</dt>
              <dd>{{ v }}</dd>
            </template>
          </dl>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('about.site') }}</span>
              <span class="hint">{{ t('about.siteHint') }}</span>
            </div>
            <a class="pill tall" :href="landingUrl">{{ t('about.siteOpen') }}</a>
          </div>
          <div v-if="legalState.docs.length" class="item">
            <div class="text">
              <span class="label">{{ t('legal.docs') }}</span>
              <span class="hint">{{ t('legal.docsHint') }}</span>
            </div>
            <span class="legal-links">
              <a v-for="d in legalState.docs" :key="d.kind" class="pill tall" :href="d.url" target="_blank" rel="noopener noreferrer" :data-legal-link="d.kind">{{ locale === 'ru' ? d.title.ru : d.title.en }}</a>
            </span>
          </div>
          <div class="item">
            <div class="text">
              <span class="hint">{{ t('about.copyHint') }}</span>
            </div>
            <button class="pill tall" @click="copyAbout"><Icon name="copy" :size="14" />{{ t('about.copy') }}</button>
          </div>
        </section>

        <section v-if="active === 'canvas'" :id="'sp-canvas'" class="group" role="tabpanel" :aria-labelledby="'st-canvas'">
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.backgroundColor') }}</span>
              <span class="hint">{{ t('settings.backgroundHint') }}</span>
            </div>
            <div class="swatches">
              <button
                v-for="c in CANVAS_BACKGROUNDS"
                :key="c"
                class="swatch-btn"
                :class="{ active: settings.background === c }"
                :style="{ background: c }"
                :aria-label="c"
                :aria-pressed="settings.background === c"
                @click="settings.background = c"
              ></button>
              <label class="swatch-btn custom" :class="{ active: !CANVAS_BACKGROUNDS.includes(settings.background) }" :title="t('export.color')">
                <input v-model="settings.background" type="color" :aria-label="t('export.color')" />
              </label>
            </div>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.grid') }}</span>
            </div>
            <div class="seg-pills" role="radiogroup" :aria-label="t('settings.grid')">
              <button role="radio" :aria-checked="!settings.editor.grid.enabled" :class="{ active: !settings.editor.grid.enabled }" @click="setGrid('none')">{{ t('props.none') }}</button>
              <button
                v-for="k in ['dots', 'lines', 'cells'] as const"
                :key="k"
                role="radio"
                :aria-checked="settings.editor.grid.enabled && settings.editor.grid.kind === k"
                :class="{ active: settings.editor.grid.enabled && settings.editor.grid.kind === k }"
                @click="setGrid(k)"
              >
                {{ t('grid.' + k) }}
              </button>
            </div>
          </div>
          <div v-if="settings.editor.grid.enabled" class="item sub">
            <div class="text">
              <span class="label">{{ t('settings.gridStep') }}</span>
            </div>
            <div class="inline">
              <input type="number" min="2" max="1000" class="num-pill" :value="settings.editor.grid.step" :aria-label="t('settings.gridStep')" @change="setStep(+($event.target as HTMLInputElement).value)" />
              <label class="swatch-btn color" :style="{ background: settings.editor.grid.color }" :title="t('grid.color')">
                <input v-model="settings.editor.grid.color" type="color" :aria-label="t('grid.color')" />
              </label>
            </div>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.snap') }}</span>
              <span class="hint">{{ t('settings.snapHint') }}</span>
            </div>
            <Toggle v-model="settings.editor.snapToGrid" :label="t('settings.snap')" />
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.smartGuides') }}</span>
              <span class="hint">{{ t('settings.smartGuidesHint') }}</span>
            </div>
            <Toggle v-model="settings.editor.smartGuides" :label="t('settings.smartGuides')" />
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.minimap') }}</span>
              <span class="hint">{{ t('settings.minimapHint') }}</span>
            </div>
            <Toggle v-model="settings.minimap" :label="t('settings.minimap')" />
          </div>
        </section>

        <section v-if="active === 'control'" :id="'sp-control'" class="group" role="tabpanel" :aria-labelledby="'st-control'">
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.emptyDrag') }}</span>
              <span class="hint">{{ t('settings.emptyDragHint') }}</span>
            </div>
            <div class="seg-pills" role="radiogroup" :aria-label="t('settings.emptyDrag')">
              <button v-for="m in ['marquee', 'pan'] as const" :key="m" role="radio" :aria-checked="settings.editor.emptyDrag === m" :class="{ active: settings.editor.emptyDrag === m }" @click="settings.editor.emptyDrag = m">
                {{ t(m === 'marquee' ? 'settings.emptyDragMarquee' : 'settings.emptyDragPan') }}
              </button>
            </div>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.emptyDbl') }}</span>
              <span class="hint">{{ t('settings.emptyDblHint') }}</span>
            </div>
            <div class="seg-pills" role="radiogroup" :aria-label="t('settings.emptyDbl')">
              <button v-for="m in ['text', 'zoom', 'none'] as const" :key="m" role="radio" :aria-checked="settings.editor.emptyDblClick === m" :class="{ active: settings.editor.emptyDblClick === m }" @click="settings.editor.emptyDblClick = m">
                {{ t(m === 'text' ? 'settings.emptyDblText' : m === 'zoom' ? 'settings.emptyDblZoom' : 'settings.emptyDblNone') }}
              </button>
            </div>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.rightDrag') }}</span>
              <span class="hint">{{ t('settings.rightDragHint') }}</span>
            </div>
            <Toggle v-model="settings.editor.rightDragPan" :label="t('settings.rightDrag')" />
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.marquee') }}</span>
              <span class="hint">{{ t('settings.marqueeHint') }}</span>
            </div>
            <div class="seg-pills" role="radiogroup" :aria-label="t('settings.marquee')">
              <button v-for="m in ['intersect', 'contain'] as const" :key="m" role="radio" :aria-checked="settings.editor.marqueeMode === m" :class="{ active: settings.editor.marqueeMode === m }" @click="settings.editor.marqueeMode = m">
                {{ t(m === 'intersect' ? 'settings.marqueeIntersect' : 'settings.marqueeContain') }}
              </button>
            </div>
          </div>
        </section>

        <section v-if="active === 'perf'" :id="'sp-perf'" class="group" role="tabpanel" :aria-labelledby="'st-perf'">
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.renderer') }}</span>
              <span class="hint">{{ t('settings.rendererHint') }}</span>
            </div>
            <div class="seg-pills" role="radiogroup" :aria-label="t('settings.renderer')">
              <button role="radio" :aria-checked="settings.renderer === 'webgl2'" :class="{ active: settings.renderer === 'webgl2' }" @click="settings.renderer = 'webgl2'">{{ t('settings.webgl') }}</button>
              <button role="radio" :aria-checked="settings.renderer === 'canvas2d'" :class="{ active: settings.renderer === 'canvas2d' }" @click="settings.renderer = 'canvas2d'">{{ t('settings.renderer2d') }}</button>
            </div>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.textLod') }}</span>
              <span class="hint">{{ t('settings.textLodHint') }}</span>
            </div>
            <div class="inline">
              <input v-model.number="settings.textLodPx" type="range" min="0" max="20" step="1" :aria-label="t('settings.textLod')" />
              <span class="value">{{ settings.textLodPx ? settings.textLodPx + ' px' : t('settings.off') }}</span>
            </div>
          </div>
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.perfPanel') }}</span>
              <span class="hint">{{ t('settings.perfPanelHint') }}</span>
            </div>
            <Toggle v-model="settings.perfPanel" :label="t('settings.perfPanel')" />
          </div>
        </section>

        <section v-if="active === 'dev'" :id="'sp-dev'" class="group" role="tabpanel" :aria-labelledby="'st-dev'">
          <div class="item">
            <div class="text">
              <span class="label">{{ t('settings.devMode') }}</span>
              <span class="hint">{{ t('settings.devModeHint') }}</span>
            </div>
            <Toggle :model-value="settings.devMode" :label="t('settings.devMode')" @update:model-value="toggleDev" />
          </div>
          <p v-if="settings.devMode" class="note warn"><Icon name="warning" :size="14" />{{ t('settings.devModeWarn') }}</p>
          <div class="item">
            <div class="text">
              <span class="label">
                {{ t('settings.verbose') }}
                <span v-if="verbose" class="badge warn">{{ t('settings.verboseLeft', { n: remaining }) }}</span>
              </span>
              <span class="hint">{{ t('settings.verboseHint') }}</span>
            </div>
            <Toggle :model-value="verbose" :label="t('settings.verbose')" @update:model-value="toggleVerbose" />
          </div>
        </section>
        <LogsView v-if="active === 'dev'" embedded />

        </div>
      </div>
    </main>
  </div>
  <Teleport to="body">
    <div v-if="securityOpen" class="dialog-backdrop" @pointerdown.self="securityOpen = false">
      <div ref="securityEl" class="dialog security" role="dialog" aria-modal="true" tabindex="-1" :aria-label="t('profile.security')" @keydown.esc.stop="securityOpen = false">
        <header class="security-head">
          <h3>{{ t('profile.security') }}</h3>
          <button type="button" class="icon-btn" :aria-label="t('common.close')" @click="securityOpen = false"><Icon name="close" :size="16" /></button>
        </header>
        <ProfileSecurity @close="securityOpen = false" />
      </div>
    </div>
    <div v-if="deleteOpen && profileState.current" class="dialog-backdrop" @pointerdown.self="deleteOpen = false">
      <div class="dialog security" role="dialog" aria-modal="true" :aria-label="t('profiles.delete')" @keydown.esc.stop="deleteOpen = false">
        <ProfileDeleteForm :profile="profileState.current" can-export @export="doExportProfile" @cancel="deleteOpen = false" @done="profileDeleted" />
      </div>
    </div>
    <div v-if="chatsOpen" class="dialog-backdrop" @pointerdown.self="chatsOpen = false">
      <div ref="chatsEl" class="dialog security chats" role="dialog" aria-modal="true" tabindex="-1" :aria-label="t('chatSettings.title')" data-dialog="chat-settings" @keydown.esc.stop="chatsOpen = false">
        <header class="security-head">
          <h3>{{ t('chatSettings.title') }}</h3>
          <button type="button" class="icon-btn" :aria-label="t('common.close')" @click="chatsOpen = false"><Icon name="close" :size="16" /></button>
        </header>
        <p v-if="!chatRows.length" class="hint chats-empty">{{ t('chatSettings.empty') }}</p>
        <ul v-else class="chat-rows">
          <li v-for="row in chatRows" :key="row.canvasId ?? ''" class="chat-row" :data-chat-canvas="row.canvasId ?? 'feed'">
            <div class="text">
              <span class="label">{{ row.name }}</span>
              <span class="hint">{{ tn('chatSettings.count', row.count) }}</span>
            </div>
            <button type="button" class="icon-btn danger" :aria-label="t('chatSettings.deleteFor', { name: row.name })" :title="t('home.delete')" @click="clearRow(row)"><Icon name="trash" :size="15" /></button>
          </li>
        </ul>
        <div class="security-actions">
          <button type="button" class="pill danger" :disabled="!chatRows.length" data-act="chat-clear-all" @click="clearChats">{{ t('chatSettings.deleteAll') }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.page {
  /* the header takes 64 px: the page gets the rest instead of a full 100% (no outer scroll) */
  flex: 1;
  min-height: 0;
  height: auto;
}
.layout {
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 8px clamp(16px, 2.6vw, 44px) 48px;
  display: grid;
  grid-template-columns: var(--nav-rail) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}
.side {
  position: sticky;
  top: 8px;
  width: var(--nav-rail);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 10px 10px;
  background: var(--panel);
  border-radius: var(--radius-panel);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 16px rgba(15, 23, 42, 0.05);
}
.title {
  margin: 0 0 10px 10px;
  font-size: 18px;
  font-weight: 600;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: var(--radius-panel);
  background: none;
  text-align: left;
  cursor: pointer;
  color: var(--text-2);
}
.nav-item:hover {
  background: var(--panel-2);
}
.nav-item.active {
  background: var(--accent-soft);
  color: var(--accent-text);
}
.nav-ico {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-panel);
  background: var(--hover);
  flex: none;
}
.nav-item.active .nav-ico {
  background: var(--hover);
  color: var(--text);
}
.nav-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 500;
}
.beta {
  padding: 1px 7px;
  border-radius: var(--radius-control);
  background: color-mix(in srgb, var(--accent) 16%, transparent);
  color: var(--accent-text);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
}
.main {
  min-width: 0;
}
.group {
  background: var(--panel);
  border-radius: var(--radius-panel);
  padding: 6px 20px;
  margin-bottom: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 16px rgba(15, 23, 42, 0.05);
}

.item {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 56px;
  padding: 10px 0;
  border-top: 1px solid var(--border);
}
.group > .item:first-child {
  border-top: none;
}
.item.sub {
  min-height: 44px;
  padding-left: 16px;
}
.avatar-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--hover);
  color: var(--text-2);
  flex: none;
  overflow: hidden;
}
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.input.name {
  width: 220px;
  height: 32px;
}
.item.actions {
  justify-content: flex-end;
  gap: 8px;
}
.pill.danger {
  color: var(--danger);
  border-color: color-mix(in srgb, var(--danger) 35%, var(--border));
}
.security {
  width: min(460px, calc(100vw - 32px));
  outline: none;
}
.security-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.security-head h3 {
  margin: 0;
}
.security-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.security-actions .pill {
  justify-content: center;
  height: 36px;
}
.chats {
  width: min(420px, calc(100vw - 32px));
}
.chats-empty {
  margin: 0 0 14px;
}
.chat-rows {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
  max-height: min(50vh, 360px);
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.chat-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: var(--radius-control);
}
.chat-row:hover {
  background: var(--hover);
}
.chat-row .label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: var(--text);
}
.hint {
  font-size: 12px;
  color: var(--text-3);
  line-height: 1.4;
}
.inline,
.swatches {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}
.custom {
  background: conic-gradient(red, yellow, lime, cyan, blue, magenta, red);
  overflow: hidden;
}
.custom input,
.color input {
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
.color {
  overflow: hidden;
}
.value {
  min-width: 48px;
  font-size: 12px;
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}
input[type='range'] {
  width: 140px;
}
.note {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin: 0 0 10px;
  padding: 10px 12px;
  border-radius: var(--radius-panel);
  font-size: 12px;
  line-height: 1.45;
}
.note.warn {
  background: var(--warn-soft);
  color: var(--warn);
}
.note :deep(svg) {
  flex: none;
  margin-top: 1px;
}
.version {
  margin: 10px 0 0 12px;
  font-size: 11px;
  color: var(--text-3);
}
/* narrow windows: the section list becomes a row of pills above the options */
@media (max-width: 760px) {
  .layout {
    /* minmax(0, …): the scrolling tab row must not stretch the page wider than the screen */
    grid-template-columns: minmax(0, 1fr);
    padding: 8px clamp(16px, 2.6vw, 44px) 48px;
  }
  .side {
    position: static;
    width: auto;
    min-width: 0;
    padding: 10px;
  }
  .side [role='tablist'] {
    display: flex;
    gap: 4px;
    overflow-x: auto;
  }
  .nav-item {
    width: auto;
    flex: none;
  }
  .version {
    display: none;
  }
}
@media (max-width: 600px) {
  .item {
    flex-wrap: wrap;
  }
  .group {
    padding: 6px 14px;
  }
}
.seg-text {
  margin-left: 5px;
}
.accent-btn {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.accent-btn:hover:not(:disabled) {
  background: var(--accent-2);
  color: var(--on-accent);
}
.danger-btn {
  color: var(--danger);
  border-color: color-mix(in srgb, var(--danger) 35%, transparent);
}
.danger-btn:hover:not(:disabled) {
  background: var(--danger-soft);
  color: var(--danger);
}
.danger-zone .label {
  color: var(--danger);
}
/* storage overview */
.usage {
  padding: 14px 0 12px;
}
.usage-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.usage-head strong {
  font-size: 22px;
  font-weight: 650;
  letter-spacing: -0.01em;
}
.usage-bar {
  display: flex;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--hover);
  gap: 2px;
}
.usage-bar span {
  min-width: 3px;
}
.legend {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 8px 18px;
}
.legend li {
  display: grid;
  grid-template-columns: 10px 1fr auto;
  align-items: center;
  column-gap: 8px;
  font-size: 12.5px;
}
.legend i {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}
.legend b {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.legend small {
  grid-column: 2 / 4;
  color: var(--text-3);
  font-size: 11.5px;
}
.item.column {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.sizes {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}
.sizes td {
  padding: 5px 8px 5px 0;
  border-top: 1px solid var(--border);
}
.sizes tr:first-child td {
  border-top: none;
}
.sizes .name {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.sizes .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.sizes .muted {
  color: var(--text-3);
}
/* about */
a.pill { text-decoration: none; }
.about-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 0 12px;
}
.about-head .label {
  font-size: 16px;
  font-weight: 650;
}
.about {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 24px;
  margin: 0;
  padding: 12px 0 14px;
  border-top: 1px solid var(--border);
  font-size: 13px;
}
.about dt {
  color: var(--text-3);
}
.about dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
  user-select: text;
}
.legal-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}
.place .folder-name {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: ui-monospace, Consolas, monospace;
  color: var(--text-2);
}
.place-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 50%;
  color: var(--text-2);
}
.place-value .folder-name {
  font-family: ui-monospace, Consolas, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.usage-who {
  display: block;
  font-size: 12.5px;
  color: var(--text-2);
}
</style>
